import {
  Box,
  Popover,
  PopoverContent,
  PopperTrigger,
  Text,
  Tooltip,
} from '@modusign/design-system';
import * as CSS from 'csstype';
import { useEffect, useState } from 'react';
import { ResponsiveValue } from 'styled-system';

import { LabelSelectPopover } from '@app/features/label/components/LabelPopovers';
import useResourceLabel from '@app/features/label/hooks/useResourceLabel';
import { InvestibleSelectableLabelId, Label } from '@app/features/label/types';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { isAxiosError } from '@app/libs/axios/isAxiosError';

interface LabelSelectButtonProps {
  nestedPopover?: boolean;
  label?: Pick<Label, 'name'> & { id: InvestibleSelectableLabelId };
  width?: ResponsiveValue<CSS.Property.Width>;
  isTooltipDisabled?: boolean;
  hidePopoverFooter?: boolean;
  onClear: () => void;
  onChange: (label: Label) => void;
  onNonLabeledSelect?: () => void;
}

export default function LabelSelectButton({
  label,
  width = '180px',
  nestedPopover,
  isTooltipDisabled = false,
  hidePopoverFooter,
  onClear,
  onChange,
  onNonLabeledSelect,
}: LabelSelectButtonProps) {
  const isDesktop = useResponsiveValue([false, false, true]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { error, data } = useResourceLabel(label?.id || '');

  useEffect(() => {
    // 라벨에 접근 권한이 사라지면 선택을 해제합니다.
    if (isAxiosError(error) && error.response?.status === 403) {
      onClear();
    }
  }, [error, onClear]);
  return (
    <Popover
      isOpen={isPopoverOpen}
      mode="click"
      nested={nestedPopover}
      placement="bottom-start"
      onOpenChange={setIsPopoverOpen}
    >
      <PopperTrigger>
        <Box>
          <Tooltip
            content={data?.name}
            disabled={!data || !isDesktop || isTooltipDisabled}
            placement="bottom"
          >
            <Box
              alignItems="center"
              as="button"
              bg="white"
              border="1px solid"
              borderColor="formBorderNeutralBase"
              borderRadius="3px"
              boxShadow="0px 1px 4px rgba(0, 0, 0, 0.0588235)"
              display="flex"
              height="36px"
              justifyContent="space-between"
              px={4}
              py={3}
              sx={{
                ':hover': {
                  backgroundColor: 'formBackgroundNeutralSlightlyDark',
                  borderColor: 'formBorderNeutralLight',
                },
              }}
              type="button"
              width={width}
            >
              <Text ellipsis>
                {data?.name || (label?.id === 'unlabeled' ? '라벨 없음' : '선택 안 함')}
              </Text>
              <svg
                fill="none"
                height="16"
                style={{ flex: 'none' }}
                viewBox="0 0 16 16"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clipRule="evenodd"
                  d="M4 6H12L8.15851 12L4 6Z"
                  fill="#474747"
                  fillRule="evenodd"
                />
              </svg>
            </Box>
          </Tooltip>
        </Box>
      </PopperTrigger>
      <PopoverContent>
        <LabelSelectPopover
          hidePopoverFooter={hidePopoverFooter}
          hidePopoverHeader={isDesktop}
          onClear={() => {
            onClear();
            setIsPopoverOpen(false);
          }}
          onClose={() => setIsPopoverOpen(false)}
          onNonLabeledSelect={onNonLabeledSelect}
          onSelect={(v) => {
            onChange(v);
            setIsPopoverOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
